import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  Button,
  Card,
  CardSubtitle,
  CardTitle,
  Field,
  Select,
  useConfirm,
  usePrompt,
  useToast,
} from '@keobook/ui';
import {
  ALLOWED_DOCUMENT_MIME,
  MAX_DOCUMENT_BYTES,
  sniffMime,
  SNIFF_CHUNK_BYTES,
  type Document,
  type DocumentKind,
} from '@keobook/shared';
import { api, putToSignedUrl } from '../lib/api';
import { useMoneyFormatter } from '../lib/useCurrency';
import { Layout } from '../components/Layout';
import { CoaPicker } from '../components/CoaPicker';
import { useBankConnections, BankConnectionsCard } from '../components/BankConnections';

const ALLOWED = new Set<string>(ALLOWED_DOCUMENT_MIME);

const KIND_OPTIONS: Array<{ value: '' | DocumentKind; label: string }> = [
  { value: '', label: 'Auto-detect' },
  { value: 'bank_statement', label: 'Bank statement' },
  { value: 'credit_card_statement', label: 'Credit card statement' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'vendor_invoice', label: 'Vendor invoice' },
  { value: 'payroll_register', label: 'Payroll register' },
  { value: '1099_nec', label: '1099-NEC' },
  { value: 'w2', label: 'W-2' },
  { value: 'brokerage_statement', label: 'Brokerage statement' },
  { value: 'other', label: 'Other' },
];

const STATUS_LABEL: Record<string, string> = {
  pending: 'Processing…',
  classified: 'Extracting transactions…',
  extracted: 'Processed',
  failed: 'Failed',
  rejected: 'Rejected',
};

const STATUS_TONE: Record<string, string> = {
  pending: 'kb-pill--warn',
  classified: 'kb-pill--warn',
  extracted: 'kb-pill--ok',
  failed: '',
  rejected: 'kb-pill--warn',
};

const KIND_LABEL = new Map(KIND_OPTIONS.map((k) => [k.value, k.label]));

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function prettySize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type Extraction = Awaited<ReturnType<typeof api.getDocumentExtraction>>;
type Proposals = Awaited<ReturnType<typeof api.getDocumentProposals>>['proposals'];

export function Documents() {
  const confirm = useConfirm();
  const toast = useToast();
  const [docs, setDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<'' | DocumentKind>('');
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [focusedDocId, setFocusedDocId] = useState<string | null>(null);
  const [extractionByDoc, setExtractionByDoc] = useState<
    Record<string, Extraction | 'loading' | 'error'>
  >({});
  const [proposalsByDoc, setProposalsByDoc] = useState<Record<string, Proposals>>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const bank = useBankConnections();

  const load = useCallback(async () => {
    try {
      setDocs((await api.getDocuments()).documents);
    } catch (err: unknown) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not load documents.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // If we landed here from a `?focus=<docId>` link (e.g. the "X transactions
  // awaiting review" card on Activity), auto-expand that document and kick
  // off the same extraction + proposals fetches `toggleExpand` does. Just
  // calling `setExpanded` is not enough — the body would render its
  // "Loading…" placeholders but no request would ever fire.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get('focus');
    if (!focusId) return;
    const target = docs.find((d) => d.id === focusId);
    if (!target) return;
    setFocusedDocId(focusId);
    if (expanded === focusId) return;
    setExpanded(focusId);
    if (target.extractionStatus !== 'extracted') return;
    void (async () => {
      try {
        const props = await api.getDocumentProposals(focusId);
        setProposalsByDoc((m) => ({ ...m, [focusId]: props.proposals }));
      } catch {
        /* polling effect below will retry */
      }
    })();
    setExtractionByDoc((m) => (m[focusId] ? m : { ...m, [focusId]: 'loading' }));
    void api
      .getDocumentExtraction(focusId)
      .then((ext) => setExtractionByDoc((m) => ({ ...m, [focusId]: ext })))
      .catch(() => setExtractionByDoc((m) => ({ ...m, [focusId]: 'error' })));
  }, [docs, expanded]);

  // Poll while anything is in flight (status pending/classified). The AI
  // pipeline typically takes 5–30 s end to end; 3 s polling keeps the UI lively
  // without hammering the API.
  useEffect(() => {
    const inFlight = docs.some(
      (d) => d.extractionStatus === 'pending' || d.extractionStatus === 'classified',
    );
    if (!inFlight) return;
    const t = window.setInterval(() => void load(), 3000);
    return () => window.clearInterval(t);
  }, [docs, load]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED.has(file.type)) {
      toast({
        tone: 'error',
        message: `Unsupported file type${file.type ? ` (${file.type})` : ''}. Try a PDF, image, or CSV.`,
      });
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (file.size > MAX_DOCUMENT_BYTES) {
      toast({
        tone: 'error',
        message: `File is too large (max ${prettySize(MAX_DOCUMENT_BYTES)}).`,
      });
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    // Magic-byte sniff: confirm the file actually IS what its mime claims
    // before uploading a single byte. Reads the first 8 KB only.
    const head = new Uint8Array(await file.slice(0, SNIFF_CHUNK_BYTES).arrayBuffer());
    const sniff = sniffMime(head, file.type);
    if (!sniff.ok) {
      toast({ tone: 'error', message: `${sniff.reason} Rename the file or pick a different one.` });
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      const sha256 = await sha256Hex(file);
      const start = await api.requestUploadUrl({
        filename: file.name,
        mime: file.type as (typeof ALLOWED_DOCUMENT_MIME)[number],
        sha256,
        sizeBytes: file.size,
      });

      if (start.alreadyExists) {
        toast({ tone: 'success', message: `"${file.name}" is already uploaded.` });
      } else {
        if (!start.uploadUrl) throw new Error('No upload URL returned.');
        await putToSignedUrl(start.uploadUrl, file, file.type);
        await api.confirmDocument({
          documentId: start.documentId,
          filename: file.name,
          mime: file.type as (typeof ALLOWED_DOCUMENT_MIME)[number],
          sha256,
          ...(kind ? { kind } : {}),
        });
        toast({ tone: 'success', message: `Uploaded "${file.name}". The AI is reading it now…` });
      }
      await load();
    } catch (err: unknown) {
      toast({ tone: 'error', message: err instanceof Error ? err.message : 'Upload failed.' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function toggleExpand(d: Document) {
    if (expanded === d.id) {
      setExpanded(null);
      return;
    }
    setExpanded(d.id);
    if (d.extractionStatus !== 'extracted') return;
    // Always re-fetch proposals on expand — categorization runs ASYNC after
    // extraction, so a previously-fetched empty list might now have data.
    void refreshProposals(d.id);
    if (extractionByDoc[d.id]) return;
    setExtractionByDoc((m) => ({ ...m, [d.id]: 'loading' }));
    try {
      const ext = await api.getDocumentExtraction(d.id);
      setExtractionByDoc((m) => ({ ...m, [d.id]: ext }));
    } catch {
      setExtractionByDoc((m) => ({ ...m, [d.id]: 'error' }));
    }
  }

  async function refreshProposals(docId: string) {
    try {
      const props = await api.getDocumentProposals(docId);
      setProposalsByDoc((m) => ({ ...m, [docId]: props.proposals }));
    } catch {
      /* keep whatever was there — categorization may still be running */
    }
  }

  // Poll for proposals while the currently-expanded document is extracted but
  // has no proposals yet — categorization runs ~10-40s after extraction
  // completes. Stops as soon as proposals arrive (or after ~2 min). We read
  // the latest proposals via a ref so the effect doesn't restart on every
  // state update (which would reset the 2-min cap).
  const proposalsRef = useRef(proposalsByDoc);
  proposalsRef.current = proposalsByDoc;
  useEffect(() => {
    if (!expanded) return;
    const d = docs.find((x) => x.id === expanded);
    if (!d || d.extractionStatus !== 'extracted') return;
    const start = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - start > 120_000) {
        window.clearInterval(id);
        return;
      }
      const have = proposalsRef.current[d.id];
      if (have && have.length > 0) {
        window.clearInterval(id);
        return;
      }
      void refreshProposals(d.id);
    }, 4000);
    return () => window.clearInterval(id);
  }, [expanded, docs]);

  async function onDownload(id: string) {
    try {
      const { url } = await api.getDocumentDownloadUrl(id);
      window.open(url, '_blank', 'noopener');
    } catch (err: unknown) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not open document.',
      });
    }
  }

  async function onDelete(d: Document) {
    const ok = await confirm({
      title: 'Delete document',
      body: (
        <>
          Permanently delete <strong>{d.filename ?? 'this document'}</strong>? This removes the file
          and any extracted transactions. You cannot undo this.
        </>
      ),
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteDocument(d.id);
      // Drop the row + any cached extraction/proposals from local state so
      // the UI updates immediately without a refetch.
      setDocs((prev) => prev.filter((x) => x.id !== d.id));
      setExtractionByDoc((m) => {
        const next = { ...m };
        delete next[d.id];
        return next;
      });
      setProposalsByDoc((m) => {
        const next = { ...m };
        delete next[d.id];
        return next;
      });
      if (expanded === d.id) setExpanded(null);
      toast({ tone: 'success', message: `"${d.filename ?? 'Document'}" deleted.` });
    } catch (err: unknown) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not delete the document.',
      });
    }
  }

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 600,
            color: 'var(--kb-gray-900)',
            margin: '0 0 1.5rem',
          }}
        >
          Documents
        </h1>

        <Card>
          <CardTitle>Upload a document</CardTitle>
          <CardSubtitle>
            Bank or credit-card statements, receipts, invoices, 1099s, W-2s. PDF, image, or CSV — up
            to {prettySize(MAX_DOCUMENT_BYTES)}. The AI classifies and extracts transactions in the
            background.
          </CardSubtitle>

          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-end',
              flexWrap: 'wrap',
              marginTop: '0.5rem',
            }}
          >
            <Field
              label="Document type (optional)"
              style={{ flex: '1 1 200px', minWidth: 0, marginBottom: 0 }}
            >
              <Select
                value={kind}
                onChange={(e) => setKind(e.currentTarget.value as '' | DocumentKind)}
              >
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value || 'auto'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <input
              ref={fileRef}
              type="file"
              onChange={onFile}
              disabled={uploading}
              accept={ALLOWED_DOCUMENT_MIME.join(',')}
              style={{ display: 'none' }}
              id="kb-file-input"
            />
            <Button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              style={{ width: 190, flexShrink: 0 }}
            >
              {uploading ? 'Uploading…' : 'Choose file & upload'}
            </Button>
            {!bank.hidden && (
              <Button
                type="button"
                variant="secondary"
                disabled={bank.connecting}
                onClick={() => void bank.connect()}
                title="Link a bank to pull statements automatically — no manual upload"
                style={{ width: 190, flexShrink: 0 }}
              >
                {bank.connecting ? 'Connecting…' : 'Connect a bank'}
              </Button>
            )}
          </div>
        </Card>

        {!bank.hidden && (
          <div style={{ marginTop: '1.25rem' }}>
            <BankConnectionsCard state={bank} />
          </div>
        )}

        <div style={{ marginTop: '1.25rem' }}>
          <Card>
            <CardTitle>Your documents</CardTitle>
            {loading ? (
              <CardSubtitle>Loading…</CardSubtitle>
            ) : docs.length === 0 ? (
              <CardSubtitle>No documents yet. Upload your first one above.</CardSubtitle>
            ) : (
              <table className="kb-report" style={{ marginTop: '0.75rem' }}>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Uploaded</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <DocumentRow
                      key={d.id}
                      d={d}
                      isExpanded={expanded === d.id}
                      focusMode={focusedDocId === d.id}
                      onToggle={() => toggleExpand(d)}
                      onDownload={() => onDownload(d.id)}
                      onDelete={() => onDelete(d)}
                      extraction={extractionByDoc[d.id]}
                      proposals={proposalsByDoc[d.id]}
                      onProposalDecided={() => refreshProposals(d.id)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}

function DocumentRow({
  d,
  isExpanded,
  focusMode,
  onToggle,
  onDownload,
  onDelete,
  extraction,
  proposals,
  onProposalDecided,
}: {
  d: Document;
  isExpanded: boolean;
  focusMode: boolean;
  onToggle: () => void;
  onDownload: () => void;
  onDelete: () => void;
  extraction: Extraction | 'loading' | 'error' | undefined;
  proposals: Proposals | undefined;
  onProposalDecided: () => void;
}) {
  const canExpand = d.extractionStatus === 'extracted';
  const isRejected = d.extractionStatus === 'rejected';
  const statusPill = STATUS_LABEL[d.extractionStatus] ?? d.extractionStatus;
  const tone = STATUS_TONE[d.extractionStatus] ?? '';

  return (
    <>
      <tr>
        <td>{d.filename ?? '(unnamed)'}</td>
        <td>{d.kind ? (KIND_LABEL.get(d.kind) ?? d.kind) : <span className="kb-muted">—</span>}</td>
        <td>
          <span className={`kb-pill ${tone || 'kb-pill--neutral'}`}>{statusPill}</span>
        </td>
        <td>{new Date(d.uploadedAt).toLocaleDateString()}</td>
        <td className="kb-report__amt" style={{ whiteSpace: 'nowrap' }}>
          {canExpand && (
            <button
              type="button"
              className="kb-linkbtn"
              onClick={onToggle}
              style={{ marginRight: 12 }}
            >
              {isExpanded ? 'Hide' : 'View'}
            </button>
          )}
          <button type="button" className="kb-linkbtn" onClick={onDownload}>
            Re-download original
          </button>
          <button
            type="button"
            className="kb-linkbtn"
            onClick={onDelete}
            style={{ marginLeft: 12, color: 'var(--kb-red-600)' }}
            aria-label={`Delete ${d.filename ?? 'document'}`}
          >
            Delete
          </button>
        </td>
      </tr>
      {isRejected && d.rejectionReason && (
        <tr>
          <td
            colSpan={5}
            style={{
              background: 'var(--kb-yellow-50, #fff7e6)',
              borderLeft: '3px solid var(--kb-yellow-500, #d97706)',
              padding: '10px 16px',
              fontSize: 13,
              color: 'var(--kb-gray-900)',
            }}
          >
            <strong style={{ color: 'var(--kb-yellow-700, #b45309)' }}>Category mismatch — </strong>
            {d.rejectionReason}
          </td>
        </tr>
      )}
      {isExpanded && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--kb-gray-50)', padding: '12px 16px' }}>
            <ExtractionDetails
              extraction={extraction}
              proposals={proposals}
              focusMode={focusMode}
              onProposalDecided={onProposalDecided}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ExtractionDetails({
  extraction,
  proposals,
  focusMode,
  onProposalDecided,
}: {
  extraction: Extraction | 'loading' | 'error' | undefined;
  proposals: Proposals | undefined;
  focusMode: boolean;
  onProposalDecided: () => void;
}) {
  // When we arrive via a "?focus=…" link, default to the pending-only view
  // so the reviewer sees only the rows that actually need a decision. The
  // toggle below lets them switch back to "all" if they want context.
  const fmt = useMoneyFormatter();
  const [pendingOnly, setPendingOnly] = useState<boolean>(focusMode);
  useEffect(() => {
    setPendingOnly(focusMode);
  }, [focusMode]);

  if (!extraction || extraction === 'loading') {
    return <p className="kb-muted">Loading extraction…</p>;
  }
  if (extraction === 'error') {
    return <p className="kb-muted">Could not load the extraction.</p>;
  }
  const txns = extraction.payload.transactions ?? [];
  if (txns.length === 0) {
    return <p className="kb-muted">The AI did not find any transactions in this document.</p>;
  }
  // Pair each extracted transaction with its matching proposal (if any) by
  // the (date | description | amount_cents) key — the proposal payload
  // carries the original transaction so this join is exact.
  const propIndex = new Map<string, Proposals[number]>();
  for (const p of proposals ?? []) {
    const t = p.transaction;
    propIndex.set(`${t.date}|${t.description}|${t.amount_cents}`, p);
  }
  const proposalsList = proposals ?? [];
  const hasProposals = proposalsList.length > 0;
  const pendingCount = proposalsList.filter((p) => p.status === 'pending').length;
  const approvedCount = proposalsList.filter((p) => p.status === 'approved').length;
  const rejectedCount = proposalsList.filter((p) => p.status === 'rejected').length;
  const visibleTxns = pendingOnly
    ? txns.filter((t) => {
        const p = propIndex.get(`${t.date}|${t.description}|${t.amount_cents}`);
        return p?.status === 'pending';
      })
    : txns;

  // Cash-flow summary (computed from extraction, not proposals — that way
  // the totals are stable even if a few transactions failed to categorize).
  let inflows = 0;
  let outflows = 0;
  for (const t of txns) {
    if (t.direction === 'credit') inflows += t.amount_cents;
    else if (t.direction === 'debit') outflows += t.amount_cents;
  }
  const net = inflows - outflows;

  return (
    <div className="kb-extract">
      {/* Focus-mode guidance: when the user arrived from the "X transactions
          awaiting your review" link, lead with what to do here. */}
      {focusMode && pendingCount > 0 && (
        <div
          role="status"
          style={{
            background: 'var(--kb-yellow-50, #fff7e6)',
            border: '1px solid var(--kb-yellow-200, #fde68a)',
            borderLeft: '3px solid var(--kb-yellow-500, #d97706)',
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--kb-yellow-700, #b45309)', marginBottom: 4 }}>
            {pendingCount} {pendingCount === 1 ? 'transaction needs' : 'transactions need'} your
            review
          </div>
          <div style={{ fontSize: 14, color: 'var(--kb-gray-900)' }}>
            The AI proposed a categorization for each but the confidence was below the auto-post
            threshold. <strong>Approve</strong> to post the entry to your journal (it will start
            showing up in Reports and Activity). <strong>Reject</strong> to skip — the transaction
            stays on file and you can re-categorize later.
          </div>
        </div>
      )}

      {/* Filter chip — show pending only / show everything */}
      {hasProposals && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            marginBottom: 10,
            fontSize: 13,
          }}
        >
          <span className="kb-muted">Show:</span>
          <button
            type="button"
            className={`kb-pill ${pendingOnly ? 'kb-pill--warn' : 'kb-pill--neutral'}`}
            style={{ cursor: 'pointer', border: 0 }}
            aria-pressed={pendingOnly}
            onClick={() => setPendingOnly(true)}
          >
            Pending ({pendingCount})
          </button>
          <button
            type="button"
            className={`kb-pill ${!pendingOnly ? 'kb-pill--ok' : 'kb-pill--neutral'}`}
            style={{ cursor: 'pointer', border: 0 }}
            aria-pressed={!pendingOnly}
            onClick={() => setPendingOnly(false)}
          >
            All ({txns.length})
          </button>
          {(approvedCount > 0 || rejectedCount > 0) && (
            <span className="kb-muted" style={{ marginLeft: 'auto' }}>
              {approvedCount > 0 && <>✓ {approvedCount} posted</>}
              {approvedCount > 0 && rejectedCount > 0 && <> · </>}
              {rejectedCount > 0 && <>× {rejectedCount} rejected</>}
            </span>
          )}
        </div>
      )}

      {/* Meta line */}
      <div className="kb-extract__meta">
        AI-extracted
        {extraction.confidence !== null && (
          <> · confidence {(extraction.confidence * 100).toFixed(0)}%</>
        )}
        {extraction.citedPages.length > 0 && (
          <>
            {' '}
            · cited page{extraction.citedPages.length > 1 ? 's' : ''}{' '}
            {extraction.citedPages.join(', ')}
          </>
        )}
        {hasProposals ? (
          <>
            {' '}
            · {pendingCount} pending · {approvedCount} posted · {rejectedCount} rejected
          </>
        ) : (
          <> · AI-prepared, not CPA-reviewed on Starter.</>
        )}
      </div>

      {/* Cash-flow summary */}
      <div className="kb-extract__summary">
        <div className="kb-extract__metric">
          <div className="kb-extract__metric-label">Inflows</div>
          <div className="kb-extract__metric-value kb-extract__metric-value--pos">
            +{fmt(inflows)}
          </div>
        </div>
        <div className="kb-extract__metric">
          <div className="kb-extract__metric-label">Outflows</div>
          <div className="kb-extract__metric-value kb-extract__metric-value--neg">
            −{fmt(outflows)}
          </div>
        </div>
        <div className="kb-extract__metric">
          <div className="kb-extract__metric-label">Net</div>
          <div
            className={`kb-extract__metric-value ${
              net >= 0 ? 'kb-extract__metric-value--pos' : 'kb-extract__metric-value--neg'
            }`}
          >
            {net >= 0 ? '+' : '−'}
            {fmt(Math.abs(net))}
          </div>
        </div>
        <div className="kb-extract__metric">
          <div className="kb-extract__metric-label">Transactions</div>
          <div className="kb-extract__metric-value">{txns.length}</div>
        </div>
      </div>

      {/* Journal entries */}
      {visibleTxns.length === 0 ? (
        <p className="kb-muted" style={{ padding: '0.75rem 0' }}>
          {pendingOnly
            ? 'Nothing left to review — every transaction is decided.'
            : 'No transactions.'}
        </p>
      ) : (
        <ol className="kb-entries">
          {visibleTxns.map((t) => {
            const p = propIndex.get(`${t.date}|${t.description}|${t.amount_cents}`);
            return (
              <JournalEntryRow
                key={`${t.date}|${t.description}|${t.amount_cents}`}
                txn={t}
                proposal={p}
                hasProposals={hasProposals}
                onDecided={onProposalDecided}
              />
            );
          })}
        </ol>
      )}

      {/* Uncategorized notice */}
      {hasProposals && proposalsList.length < txns.length && (
        <p className="kb-extract__warn-foot">
          {txns.length - proposalsList.length} transaction
          {txns.length - proposalsList.length === 1 ? '' : 's'} could not be confidently categorized
          — a CPA reviewer will handle them.
        </p>
      )}
    </div>
  );
}

function JournalEntryRow({
  txn,
  proposal,
  hasProposals,
  onDecided,
}: {
  txn: { date: string; description: string; amount_cents: number; direction?: 'debit' | 'credit' };
  proposal: Proposals[number] | undefined;
  hasProposals: boolean;
  onDecided: () => void;
}) {
  const fmt = useMoneyFormatter();
  const prompt = usePrompt();
  const toast = useToast();
  const [showRationale, setShowRationale] = useState(false);
  const [busy, setBusy] = useState<null | 'approve' | 'reject'>(null);
  const sign = txn.direction === 'debit' ? '−' : txn.direction === 'credit' ? '+' : '';
  const tone = txn.direction === 'credit' ? 'pos' : txn.direction === 'debit' ? 'neg' : 'neutral';
  const lineAmount = fmt(Math.abs(txn.amount_cents));
  const debit = proposal?.proposedEntry.lines.find((l) => l.side === 'debit');
  const credit = proposal?.proposedEntry.lines.find((l) => l.side === 'credit');
  const lowConfidence = proposal && proposal.confidence < 0.7;
  const isPending = proposal?.status === 'pending';
  const isApproved = proposal?.status === 'approved';
  const isRejected = proposal?.status === 'rejected';

  async function onApprove() {
    if (!proposal) return;
    setBusy('approve');
    try {
      await api.approveProposal(proposal.id);
      toast({ tone: 'success', message: 'Posted to journal.' });
      onDecided();
    } catch (err: unknown) {
      toast({ tone: 'error', message: err instanceof Error ? err.message : 'Could not approve.' });
    } finally {
      setBusy(null);
    }
  }

  async function onReject() {
    if (!proposal) return;
    const reason = await prompt({
      title: 'Reject proposal',
      body: 'Optional: tell us why this categorization is wrong. Leave blank to skip.',
      placeholder: 'e.g. wrong account — should be 6200, not 6700',
      confirmLabel: 'Reject',
      destructive: true,
    });
    if (reason === null) return; // user cancelled
    setBusy('reject');
    try {
      await api.rejectProposal(proposal.id, reason || undefined);
      toast({ tone: 'success', message: 'Proposal rejected.' });
      onDecided();
    } catch (err: unknown) {
      toast({ tone: 'error', message: err instanceof Error ? err.message : 'Could not reject.' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <li className="kb-entry">
      <div className="kb-entry__head">
        <span className="kb-entry__date">{txn.date}</span>
        <span className="kb-entry__desc">{txn.description}</span>
        <span className={`kb-entry__amt kb-entry__amt--${tone}`}>
          {sign}
          {lineAmount}
        </span>
      </div>
      {proposal ? (
        <>
          <div className="kb-entry__line">
            <span className="kb-entry__side">Dr</span>
            <span className="kb-entry__name" style={{ gridColumn: 'span 2' }}>
              {isPending ? (
                <CoaPicker
                  currentCode={debit?.account_code ?? ''}
                  currentLabel={debit?.account_name}
                  target={{ mode: 'proposal', proposalId: proposal.id, side: 'debit' }}
                  onDone={() => onDecided()}
                />
              ) : (
                <>
                  <span className="kb-entry__code">{debit?.account_code}</span>{' '}
                  {debit?.account_name}
                </>
              )}
            </span>
            <span className="kb-entry__line-amt">{lineAmount}</span>
            <span className="kb-entry__line-amt-credit" />
          </div>
          <div className="kb-entry__line">
            <span className="kb-entry__side">Cr</span>
            <span className="kb-entry__name" style={{ gridColumn: 'span 2' }}>
              {isPending ? (
                <CoaPicker
                  currentCode={credit?.account_code ?? ''}
                  currentLabel={credit?.account_name}
                  target={{ mode: 'proposal', proposalId: proposal.id, side: 'credit' }}
                  onDone={() => onDecided()}
                />
              ) : (
                <>
                  <span className="kb-entry__code">{credit?.account_code}</span>{' '}
                  {credit?.account_name}
                </>
              )}
            </span>
            <span className="kb-entry__line-amt" />
            <span className="kb-entry__line-amt-credit">{lineAmount}</span>
          </div>
          <div className="kb-entry__foot">
            {lowConfidence ? (
              <span className="kb-entry__confidence kb-entry__confidence--low">
                Low confidence · {Math.round(proposal.confidence * 100)}%
              </span>
            ) : (
              <span className="kb-entry__confidence">
                Confidence {Math.round(proposal.confidence * 100)}%
              </span>
            )}
            {isApproved && (
              <span className="kb-pill kb-pill--ok" style={{ marginLeft: 8 }}>
                ✓ Posted
              </span>
            )}
            {isRejected && (
              <span className="kb-pill kb-pill--warn" style={{ marginLeft: 8 }}>
                × Rejected
              </span>
            )}
            {proposal.rationale && (
              <button
                type="button"
                className="kb-linkbtn"
                onClick={() => setShowRationale((v) => !v)}
              >
                {showRationale ? 'Hide reasoning' : 'Why this categorization?'}
              </button>
            )}
            {isPending && (
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="kb-button kb-button--primary kb-button--sm"
                  disabled={busy !== null}
                  onClick={onApprove}
                  aria-label={`Approve ${txn.description}`}
                >
                  {busy === 'approve' ? 'Posting…' : 'Approve & post'}
                </button>
                <button
                  type="button"
                  className="kb-button kb-button--ghost kb-button--sm"
                  disabled={busy !== null}
                  onClick={onReject}
                  aria-label={`Reject ${txn.description}`}
                >
                  {busy === 'reject' ? 'Rejecting…' : 'Reject'}
                </button>
              </div>
            )}
          </div>
          {showRationale && proposal.rationale && (
            <p className="kb-entry__rationale">{proposal.rationale}</p>
          )}
        </>
      ) : hasProposals ? (
        <p className="kb-entry__pending">No confident categorization — flagged for CPA review.</p>
      ) : null}
    </li>
  );
}
